import { useEffect, useState } from "react";
import { Loader2, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/services/apiClient";
import DataListPage from "./DataListPage";
import ToastHelper from "@/utils/toastHelper";
import { parseCalendarDate } from "@/utils/dateUtils";
import { getStageUrl } from "@/components/course-progress/courseProgressNavigation";

export default function AdminClasses() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);
  const [studentDialog, setStudentDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lookups, setLookups] = useState({ courses: [], campuses: [], trainers: [] });

  const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");

  const mapClass = (item) => ({
    id: item.scheduleCode || `C-${item.courseScheduleId}`,
    course: item.courseName,
    courseFee: Number(item.courseFee || 0),
    date: formatScheduleDate(item.scheduleDate),
    time: `${item.startTime || "-"} - ${item.endTime || "-"}`,
    location: item.campusName,
    trainer: item.trainerName,
    booked: Number(item.bookedSeats || 0),
    capacity: Number(item.maxSeats || 0),
    status: Number(item.statusNo) === 1 ? "Active" : Number(item.statusNo) === 2 ? "Inactive" : "None",
    canView: item.canView !== false,
    canEdit: item.canEdit === true,
    canActivate: item.canActivate === true,
    canDeactivate: item.canDeactivate === true,
    raw: item,
  });
  const sortClasses = (items) => [...items].sort((left, right) => {
    const rightDate = parseCalendarDate(right.raw?.scheduleDate || 0)?.getTime() || 0;
    const leftDate = parseCalendarDate(left.raw?.scheduleDate || 0)?.getTime() || 0;
    return rightDate - leftDate;
  });

  useEffect(() => {
    const loadClasses = async () => {
      try {
        const response = await apiClient.get(`${apiBaseUrl}/AdminClass/GetClasses`, { params: { page: 1, pageSize: 100 } });
        const classes = Array.isArray(response.data?.classes) ? response.data.classes : [];
        setRows(sortClasses(classes.map(mapClass)));
        setLookups({
          courses: Array.isArray(response.data?.courses) ? response.data.courses : [],
          campuses: Array.isArray(response.data?.campuses) ? response.data.campuses : [],
          trainers: Array.isArray(response.data?.trainers) ? response.data.trainers : [],
        });
      } catch (requestError) {
        console.error("Failed to load classes", requestError);
        setError(requestError.response?.data?.message || "Unable to load classes.");
      }
    };
    loadClasses();
  }, [apiBaseUrl]);

  const openDialog = (row, mode) => setDialog({ mode, item: { ...row.raw } });
  const openStudentList = async (row) => {
    setStudentDialog({ row, students: [], loading: true });
    try {
      const response = await apiClient.get(`${apiBaseUrl}/AdminClass/${row.raw.courseScheduleId}/Students`);
      const students = Array.isArray(response.data?.students) ? response.data.students : [];
      setStudentDialog({ row, students, loading: false });
    } catch (requestError) {
      setStudentDialog(null);
      ToastHelper.apiError(requestError, "Unable to load class students");
    }
  };
  const openAddDialog = () => {
    const selectedDate = getLocalDateValue(new Date());
    setDialog({ mode: "add", item: {
      courseScheduleId: 0,
      courseId: lookups.courses[0]?.id || 0,
      campusId: lookups.campuses[0]?.id || 0,
      trainerId: lookups.trainers[0]?.id || 0,
      scheduleDate: toLocalDateTimeOffset(selectedDate),
      scheduleCode: scheduleCodeFromDate(selectedDate),
      startTime: "15:00", endTime: "15:30", maxSeats: 40, bookedSeats: 0,
      courseFee: Number(lookups.courses[0]?.fee || 0), status: "Active", statusNo: 1, remarks: "",
      courseName: "", campusName: "", trainerName: "",
    }});
  };
  const updateField = (key, value) => setDialog((current) => ({ ...current, item: { ...current.item, [key]: value } }));
  const changeStatus = async (row, statusNo) => {
    const item = { ...row.raw, statusNo, status: statusNo === 1 ? "Active" : "Inactive" };
    setSaving(true);
    try {
      await apiClient.put(`${apiBaseUrl}/AdminClass/${item.courseScheduleId}`, item);
      setRows((current) => sortClasses(current.map((currentRow) => currentRow.raw.courseScheduleId === item.courseScheduleId ? mapClass(item) : currentRow)));
      ToastHelper.success(`Class marked ${item.status}`);
    } catch (requestError) {
      ToastHelper.apiError(requestError, "Unable to change class status");
    } finally {
      setSaving(false);
    }
  };

  const saveClass = async () => {
    setSaving(true);
    try {
      if (dialog.mode === "add") {
        const response = await apiClient.post(`${apiBaseUrl}/AdminClass`, dialog.item);
        const created = response.data;
        created.courseName = lookups.courses.find((x) => x.id === created.courseId)?.name || "";
        created.campusName = lookups.campuses.find((x) => x.id === created.campusId)?.name || "";
        created.trainerName = lookups.trainers.find((x) => x.id === created.trainerId)?.name || "";
        setRows((current) => sortClasses([...current, mapClass(created)]));
        ToastHelper.success("Class scheduled successfully");
      } else {
        await apiClient.put(`${apiBaseUrl}/AdminClass/${dialog.item.courseScheduleId}`, dialog.item);
        setRows((current) => sortClasses(current.map((row) => row.raw.courseScheduleId === dialog.item.courseScheduleId ? mapClass(dialog.item) : row)));
        ToastHelper.success("Class updated successfully");
      }
      setDialog(null);
    } catch (requestError) {
      ToastHelper.apiError(requestError, "Unable to update class");
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;

  return <>
  <DataListPage
    title="Classes" subtitle="Scheduled classes across all locations" testidPrefix="classes"
    rows={rows}
    paginated
    columnFilters
    statusStyles={{ Active: "bg-emerald-50 text-emerald-700 border border-emerald-200", InActive: "bg-rose-50 text-rose-700 border border-rose-200", None: "bg-slate-100 text-slate-700 border border-slate-200" }}
    addButton="+ Schedule class"
    onAdd={openAddDialog}
    actions={[
      { label: "View students", fn: openStudentList },
      { label: "View", fn: (row) => openDialog(row, "view"), hidden: (row) => !row.canView },
      { label: "Edit", fn: (row) => openDialog(row, "edit"), hidden: (row) => !row.canEdit },
      { label: "Activate", fn: (row) => changeStatus(row, 1), hidden: (row) => row.status === "Active" || !row.canActivate || saving },
      { label: "Deactivate", fn: (row) => changeStatus(row, 2), hidden: (row) => row.status !== "Active" || !row.canDeactivate || saving },
    ]}
    columns={[
      { key: "id", label: "Class ID" },
      { key: "course", label: "Course" },
      { key: "courseFee", label: "Course fee", align: "right", render: (r) => `$${Number(r.raw?.courseFee ?? 0).toFixed(2)}` },
      { key: "date", label: "Date" },
      { key: "time", label: "Time" },
      { key: "location", label: "Location", render: (r) => <span className="font-semibold">{r.location}</span> },
      { key: "trainer", label: "Trainer" },
      { key: "booked", label: "Booked / Cap", align: "right", render: (r) => <button type="button" className="inline-flex min-w-14 items-center justify-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700 transition hover:border-orange-400 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-300" onClick={() => openStudentList(r)} title="View class students">{r.booked}/{r.capacity}</button> },
      { key: "status", label: "Status", type: "status" },
    ]}
  />
  {dialog && <ClassDialog dialog={dialog} lookups={lookups} onClose={() => setDialog(null)} onChange={updateField} onSave={saveClass} saving={saving} />}
  {studentDialog && <ClassStudentsDialog dialog={studentDialog} onClose={() => setStudentDialog(null)} onGoToStep={(student) => {
    const progressUrl = `/admin/caurse-progress/${student.studentBookingId}`;
    const stepUrl = getStageUrl(student.currentStepUrl || "", "admin", student.studentBookingId);
    navigate(stepUrl || progressUrl, { state: { focusCurrentStep: true, focusCourseStepTypeNo: student.currentStepTypeNo || null } });
  }} />}
  </>;
}

function ClassStudentsDialog({ dialog, onClose, onGoToStep }) {
  const { row, students, loading } = dialog;
  const [filters, setFilters] = useState({});
  const visibleStudents = students.filter((student) => Object.entries(filters).every(([key, filterValue]) => {
    const value = key === "student"
      ? `${student.studentName || ""} ${student.studentId || ""}`
      : key === "contact"
        ? `${student.email || ""} ${student.mobileNumber || ""}`
        : key === "booking"
          ? `${student.bookingNumber || ""} ${student.bookingStatus || ""}`
          : key === "workflow"
            ? student.workFlowStatusName || ""
            : key === "action"
              ? `${student.currentStepName || ""} ${student.currentStepStatus || ""} ${student.currentStepDescription || ""}`
              : key === "payment"
                ? student.paymentStatus || ""
                : student.bookingDate ? new Date(student.bookingDate).toLocaleDateString("en-AU") : "";
    return !filterValue || String(value).toLowerCase().includes(String(filterValue).toLowerCase());
  }));
  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));
  const filterInput = (key, placeholder = "Filter") => <input value={filters[key] || ""} onChange={(event) => setFilter(key, event.target.value)} placeholder={placeholder} className="h-8 w-full min-w-28 rounded-md border border-slate-200 px-2 text-xs font-normal normal-case tracking-normal text-slate-600 outline-none placeholder:text-slate-400 focus:border-orange-400" aria-label={`Filter ${placeholder}`} />;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <div className="w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-slate-200 bg-[#0A2540] px-6 py-5 text-white">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><Users className="h-5 w-5" /></div>
          <div>
            <h2 className="text-xl font-display font-extrabold">Class students</h2>
            <p className="mt-1 text-sm text-slate-300">{row.raw.courseName} · {row.raw.scheduleCode}</p>
          </div>
        </div>
        <button className="text-2xl leading-none text-slate-300 hover:text-white" onClick={onClose}>×</button>
      </div>
      <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-6 py-4 sm:grid-cols-4">
        <ClassSummary label="Class date" value={row.date} />
        <ClassSummary label="Time" value={row.time} />
        <ClassSummary label="Location" value={row.location} />
        <ClassSummary label="Students" value={<span className="inline-flex min-w-14 items-center justify-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-bold text-orange-700">{students.length} / {row.capacity}</span>} />
      </div>
      <div className="max-h-[60vh] overflow-auto p-6">
        {loading ? <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading students...</div> : students.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 px-4 py-14 text-center text-sm text-slate-500">No students are booked into this class.</div> : <div className="overflow-x-auto rounded-xl border border-slate-200"><table className="w-full min-w-[1120px] text-left text-sm"><thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Student</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Booking</th><th className="px-4 py-3">Enrolment workflow</th><th className="px-4 py-3">Next required action</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Booked on</th><th className="px-4 py-3"></th></tr><tr className="border-t border-slate-200 bg-white"><th className="px-2 pb-3 pt-2">{filterInput("student")}</th><th className="px-2 pb-3 pt-2">{filterInput("contact")}</th><th className="px-2 pb-3 pt-2">{filterInput("booking")}</th><th className="px-2 pb-3 pt-2">{filterInput("workflow")}</th><th className="px-2 pb-3 pt-2">{filterInput("action")}</th><th className="px-2 pb-3 pt-2">{filterInput("payment")}</th><th className="px-2 pb-3 pt-2">{filterInput("booked on")}</th><th /></tr></thead><tbody className="divide-y divide-slate-100">{visibleStudents.map((student) => <tr key={student.studentBookingId} className="align-top hover:bg-slate-50"><td className="px-4 py-3"><div className="font-semibold text-[#0A2540]">{student.studentName || "-"}</div><div className="mt-1 text-xs text-slate-500">Student #{student.studentId}</div></td><td className="px-4 py-3"><div>{student.email || "-"}</div><div className="mt-1 text-xs text-slate-500">{student.mobileNumber || "-"}</div></td><td className="px-4 py-3"><div className="font-medium">{student.bookingNumber || "-"}</div><div className="mt-1 text-xs text-slate-500">{student.bookingStatus || "-"}</div></td><td className="px-4 py-3"><span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">{student.workFlowStatusName || "Not started"}</span></td><td className="min-w-56 px-4 py-3"><div className="font-semibold text-[#0A2540]">{student.currentStepName || "No pending action"}</div><div className="mt-1"><span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">{student.currentStepStatus || "-"}</span></div><div className="mt-2 text-xs text-slate-600">{student.currentStepDescription || "No further action is required."}</div>{student.currentStepName && student.currentStepName !== "Completed" && <div className="mt-1 text-xs font-semibold text-orange-700">Please complete before class on {row.date}</div>}</td><td className="px-4 py-3"><span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">{student.paymentStatus || "-"}</span></td><td className="px-4 py-3 text-slate-600">{student.bookingDate ? new Date(student.bookingDate).toLocaleDateString("en-AU") : "-"}</td><td className="px-4 py-3 text-right"><button type="button" className="wc-btn-outline whitespace-nowrap px-3 py-1.5 text-xs" onClick={() => onGoToStep(student)}>Go to step</button></td></tr>)}</tbody></table></div>}
      </div>
      <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-4"><button className="wc-btn-outline" onClick={onClose}>Close</button></div>
    </div>
  </div>;
}

function ClassSummary({ label, value }) {
  return <div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div><div className="mt-1 truncate text-sm font-semibold text-[#0A2540]" title={typeof value === "string" ? value : undefined}>{value || "-"}</div></div>;
}

function ClassDialog({ dialog, lookups, onClose, onChange, onSave, saving }) {
  const item = dialog.item;
  const editing = dialog.mode === "edit" || dialog.mode === "add";
  const dateValue = getDatePart(item.scheduleDate);
  const fields = [
    ["scheduleCode", "Schedule code", "text"],
    ["scheduleDate", "Schedule date", "date"], ["startTime", "Start time", "time"],
    ["endTime", "End time", "time"], ["maxSeats", "Maximum seats", "number"],
    ["bookedSeats", "Booked seats", "number"], ["courseFee", "Course fee", "number"],
  ];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
      <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
        <div><h2 className="text-xl font-display font-extrabold text-[#0A2540]">{dialog.mode === "add" ? "Schedule class" : editing ? "Edit class" : "Class details"}</h2><p className="text-sm text-slate-500">{dialog.mode === "add" ? "Create a new course schedule" : `${item.courseName} · ${item.campusName}`}</p></div>
        <button className="text-2xl text-slate-400 hover:text-slate-700" onClick={onClose}>×</button>
      </div>
      <div className="grid gap-4 p-6 sm:grid-cols-2">
        <LookupField label="Course" value={item.courseId} options={lookups.courses} disabled={!editing} onChange={(value) => { onChange("courseId", value); onChange("courseFee", Number(lookups.courses.find(option => option.id === value)?.fee || 0)); }} />
        <LookupField label="Campus" value={item.campusId} options={lookups.campuses} disabled={!editing} onChange={(value) => onChange("campusId", value)} />
        <LookupField label="Trainer" value={item.trainerId} options={lookups.trainers} disabled={!editing} onChange={(value) => onChange("trainerId", value)} />
        <LookupField label="Status" value={item.statusNo} options={[{ id: 0, name: "None" }, { id: 1, name: "Active" }, { id: 2, name: "InActive" }]} disabled={!editing} onChange={(value) => { onChange("statusNo", value); }} />
        {fields.map(([key, label, type]) => <label key={key} className="block"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{label}</span><input className="wc-input disabled:bg-slate-100 disabled:text-slate-600" type={type} min={key === "maxSeats" ? 1 : key === "courseFee" ? 0 : undefined} max={key === "maxSeats" ? 40 : undefined} disabled={!editing || key === "scheduleCode"} readOnly={key === "scheduleCode"} step={key === "courseFee" ? "0.01" : undefined} value={key === "scheduleDate" ? dateValue : (item[key] ?? "")} onChange={(e) => { if (key === "scheduleDate") { const code = scheduleCodeFromDate(e.target.value); onChange("scheduleDate", toLocalDateTimeOffset(e.target.value)); onChange("scheduleCode", code); } else onChange(key, type === "number" ? Number(e.target.value) : e.target.value); }} /></label>)}
        <label className="block sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">Remarks</span><textarea className="wc-input min-h-24" disabled={!editing} value={item.remarks || ""} onChange={(e) => onChange("remarks", e.target.value)} /></label>
      </div>
      <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-white px-6 py-4"><button className="wc-btn-outline" onClick={onClose}>Close</button>{editing && <button className="wc-btn-primary" disabled={saving} onClick={onSave}>{saving ? "Saving..." : "Save changes"}</button>}</div>
    </div>
  </div>;
}

function LookupField({ label, value, options, disabled, onChange }) {
  return <label className="block"><span className="mb-1 block text-xs font-semibold uppercase text-slate-500">{label}</span><select className="wc-input" disabled={disabled} value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))}><option value="">Select {label.toLowerCase()}</option>{options.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>;
}


function getDatePart(value) {
  if (!value) return "";

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function getLocalDateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function toLocalDateTimeOffset(dateValue) {
  if (!dateValue) return null;

  const [year, month, day] = dateValue.split("-").map(Number);
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  const offsetMinutes = -localDate.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, "0");
  const offsetMins = String(absoluteOffset % 60).padStart(2, "0");

  return `${dateValue}T00:00:00${sign}${offsetHours}:${offsetMins}`;
}

function formatScheduleDate(value) {
  const dateValue = getDatePart(value);
  if (!dateValue) return "-";

  const [year, month, day] = dateValue.split("-");
  return `${day}/${month}/${year}`;
}

function scheduleCodeFromDate(date) {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `WC-${day}${month}${year.slice(-2)}`;
}
